import { useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, UsersApi } from '@setvi/shared/enums';

export interface CompanyUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  salesRepId?: string;
}

const PAGE_SIZE = 20;

const fetchCompanyUsers = async (offset: number, searchStr: string) => {
  const response = await axiosHelper({
    baseURL: process.env.REVOPS_API,
    endpoint: UsersApi.CompanySearch,
    method: AxiosMethods.POST,
    body: { limit: PAGE_SIZE, offset, searchStr: searchStr || '' }
  });

  // PaginatedResponse<User> — the array lives on `data` (same as /user/search)
  return (response?.data || []) as CompanyUser[];
};

export const useCompanyUserSearch = (searchStr: string) => {
  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteQuery(
    ['rfq_company_user_search', searchStr],
    ({ pageParam = 0 }) => fetchCompanyUsers(pageParam, searchStr),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage?.length < PAGE_SIZE ? undefined : allPages.length * PAGE_SIZE,
      cacheTime: 0
    }
  );

  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const users = data?.pages.flatMap(page => page) || [];

  return { users, isLoading, fetchMore };
};
